import { ErrorBoundary } from '@sentry/react';
import { lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAdminStore } from '@/store/admin';

import { ErrorContent } from '@/components/ErrorContent';
import { RequireAuth } from '@/routes/RequireAuth';

// Bot Detail

const LazyChatPage = lazy(() => import('@/pages/Bot/Chat'));
const LazyDashboardPage = lazy(() => import('@/pages/Bot/Dashboard'));
const LazyVisitorsPage = lazy(() => import('@/pages/Bot/Visitors'));
const LazyDataSourcePage = lazy(() => import('@/pages/Bot/DataSource'));
// const LazyTeachModelPage = lazy(() => import('@/pages/Bot/TeachModel'));
const LazySettingsPage = lazy(() => import('@/pages/Bot/Settings'));
const LazyToolsPage = lazy(() => import('@/pages/Bot/Tools'));
const LazyWidgetEmbedPage = lazy(() => import('@/pages/Bot/WidgetEmbed'));
const LazyQuestionsPage = lazy(() => import('@/pages/Bot/Questions'));
const LazyInsightsPage = lazy(() => import('@/pages/Bot/Insights'));
const LazyUsagePage = lazy(() => import('@/pages/Bot/Usage'));
const LazyDictionaryPage = lazy(() => import('@/pages/Bot/Dictionary'));
const LazyRecordsPage = lazy(() => import('@/pages/Bot/Records'));
const LazyTagPage = lazy(() => import('@/pages/Bot/Tag'));

export const BotRoutes = () => {
  return (
    <Routes>
      <Route path="/chat" element={<LazyChatPage />} />
      <Route path="/dashboard" element={<LazyDashboardPage />} />
      <Route path="/visitors" element={<LazyVisitorsPage />} />
      <Route path="/data-source" element={<LazyDataSourcePage />} />
      {/* <Route path="/teach-model" element={<LazyTeachModelPage />} /> */}
      <Route path="/settings" element={<LazySettingsPage />} />
      <Route path="/tools" element={<LazyToolsPage />} />
      <Route path="/widget-embed" element={<LazyWidgetEmbedPage />} />
      <Route path="/questions" element={<LazyQuestionsPage />} />
      <Route path="/insights" element={<LazyInsightsPage />} />
      <Route path="/usage" element={<LazyUsagePage />} />
      <Route path="/dictionary" element={<LazyDictionaryPage />} />
      <Route path="/records" element={<LazyRecordsPage />} />
      <Route path="/datasets/:datasetId/*" element={<LazyDatasets />} />
      <Route path="/tag" element={<LazyTagPage />} />

      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );
};

export const AssistRoutes = () => {
  return (
    <Routes>
      <Route path="/chat" element={<LazyChatPage />} />
      <Route path="/dashboard" element={<LazyDashboardPage />} />
      <Route path="/visitors" element={<LazyVisitorsPage />} />
      <Route path="/data-source" element={<LazyDataSourcePage />} />
      {/* <Route path="/teach-model" element={<LazyTeachModelPage />} /> */}
      <Route path="/settings" element={<LazySettingsPage />} />
      <Route path="/tools" element={<LazyToolsPage />} />
      <Route path="/widget-embed" element={<LazyWidgetEmbedPage />} />
      <Route path="/questions" element={<LazyQuestionsPage />} />
      <Route path="/insights" element={<LazyInsightsPage />} />
      <Route path="/usage" element={<LazyUsagePage />} />
      <Route path="/dictionary" element={<LazyDictionaryPage />} />
      <Route path="/records" element={<LazyRecordsPage />} />
      <Route path="/datasets/:datasetId/*" element={<LazyDatasets />} />
      <Route path="/tag" element={<LazyTagPage />} />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );
};

// Datasets
const LazyDataSourcesPage = lazy(() => import('@/pages/Datasets/DataSources'));
const LazyDataBaseImportPage = lazy(() => import('@/pages/Datasets/DataBase/import'));
const LazyDataBasePropertyPage = lazy(() => import('@/pages/Datasets/DataBase/property'));
export const DatasetsRoutes = () => {
  return (
    <Routes>
      <Route path="/data-source/*" element={<LazyDataSourcesPage />} />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );
};

// Datasets Sources
const LazyDataBasePage = lazy(() => import('@/pages/Datasets/DataBase'));
const LazyAPIsPage = lazy(() => import('@/pages/Datasets/APIs'));
const LazyFileChunksPage = lazy(() => import('@/pages/Datasets/Chunk'));
const LazyDataSourcesListPage = lazy(() => import('@/pages/Datasets/DataSources/list'));
const LazyIntegrationTasksPage = lazy(() => import('@/pages/Datasets/Integration/Tasks'));

export const DatasetSourcesRoutes = () => {
  return (
    <Routes>
      <Route path="/faq" element={<LazyDataBasePage />} />
      <Route path="/api" element={<LazyAPIsPage />} />
      <Route path="/integration" element={<LazyIntegrationTasksPage />} />
      <Route path="/integration/:ruleId" element={<LazyDataSourcesListPage />} />
      <Route path="/integration/:ruleId/chunk/:fileId" element={<LazyFileChunksPage />} />
      <Route path="/data-base/import" element={<LazyDataBaseImportPage />} />
      <Route path="/data-base/property" element={<LazyDataBasePropertyPage />} />
      <Route path="/data-base" element={<LazyDataBasePage />} />
      <Route path="/file/:fileId" element={<LazyFileChunksPage />} />
      <Route path="/website/:fileId" element={<LazyFileChunksPage />} />
      <Route path="/*" element={<LazyDataSourcesListPage />} />
    </Routes>
  );
};

// Main

const LazyBotsPage = lazy(() => import('@/pages/Bots'));
const LazyBotPage = lazy(() => import('@/pages/Bot'));
const LazyTeamPage = lazy(() => import('@/pages/Team'));
const LazyAccountPage = lazy(() => import('@/pages/Account'));
const LazyApiKeysPage = lazy(() => import('@/pages/ApiKeys'));
const LazyCreatePage = lazy(() => import('@/pages/Create'));
const LazyDatasets = lazy(() => import('@/pages/Datasets'));
const LazyCollaborativePage = lazy(() => import('@/pages/Collaborative'));
const LazyAccessPage = lazy(() => import('@/pages/Access'));
const LazyAssistPage = lazy(() => import('@/pages/Assist'));
const LazyAdminConsolePage = lazy(() => import('@/pages/AdminConsole'));

const ProtectedRoute = ({ element, hasAccess }: { element: JSX.Element; hasAccess: boolean }) => {
  return hasAccess ? element : <Navigate to="/no-access" replace />;
};

export const MainRoutes = () => {
  const { hasAdminConsoleAuthority } = useAdminStore();

  return (
    <Routes>
      <Route path="/create" element={<LazyCreatePage />} />
      <Route path="/bots" element={<LazyBotsPage />} />
      <Route path="/bot/:id/*" element={<LazyBotPage />} />
      <Route path="/datasets/:datasetId/*" element={<LazyDatasets />} />
      <Route path="/datasets" element={<LazyDatasets />} />
      <Route path="/team" element={<LazyTeamPage />} />
      <Route path="/account" element={<LazyAccountPage />} />
      <Route path="/keys" element={<LazyApiKeysPage />} />
      <Route path="/collaborative" element={<LazyCollaborativePage />} />
      <Route path="/access" element={<LazyAccessPage />} />
      <Route path="/assist/:id/*" element={<LazyAssistPage />} />
      <Route path="/admin-console" element={<LazyAdminConsolePage />} />
      {/* <Route
        path="/admin-console"
        element={<ProtectedRoute element={<LazyAdminConsolePage />} hasAccess={!!hasAdminConsoleAuthority} />}
      /> */}
      <Route path="/" element={<Navigate to="/bots" replace />} />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );
};

const LazyLogin = lazy(() => import('@/pages/Login'));
const LazyLoginCallbackPage = lazy(() => import('@/pages/LoginCallback'));
const LazyNotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const LazyMainPage = lazy(() => import('@/pages/Main'));
const LazyDebugChunksPage = lazy(() => import('@/pages/Debug/Chunks'));
const LazyWidgetPreviewPage = lazy(() => import('@/pages/Bot/WidgetPreview'));
const LazyNoAccessPage = lazy(() => import('@/pages/NoAccess'));

export const AppRoutes = () => {
  return (
    <ErrorBoundary fallback={({ error }) => <ErrorContent error={error} />}>
      <Routes>
        <Route path="/login" element={<LazyLoginCallbackPage />} />
        <Route path="/auth/login" element={<LazyLogin />} />
        <Route path="/404" element={<LazyNotFoundPage />} />
        <Route path="/debug/chunks" element={<LazyDebugChunksPage />} />
        <Route path="/widget-preview" element={<LazyWidgetPreviewPage />} />
        <Route path="/no-access" element={<LazyNoAccessPage />} />
        <Route
          path="*"
          element={
            <RequireAuth>
              <LazyMainPage />
            </RequireAuth>
          }
        />
      </Routes>
    </ErrorBoundary>
  );
};
